import React from 'react';

import { Grid, Typography } from '@mui/material';

import BackButton from '../../BackButton';
import ContentCard, { ContentCardProps } from '../../ContentCard';
import {
  BackButton as WrapperBackButton,
  Caption as WrapperCaption,
  Content as WrapperContent,
  Title as WrapperTitle,
  TitleContainer,
} from '../../ContentWrapper/index.styles';
import { SELECT_WIDTH } from '../../Select';
import {
  SkeletonBox,
  SkeletonButton,
  SkeletonChip,
  SkeletonCircle,
  SkeletonGroup,
  SkeletonHeading,
  SkeletonIconButton,
  SkeletonInput,
  SkeletonParagraph,
  SkeletonText,
} from '..';
import { SELECT_HEIGHT, TABLE_ROW_HEIGHT } from '../constants';
import { SkeletonSize } from '../types';
import { getVariedWidth } from '../utils';

import {
  Axis,
  CalendarColumn,
  CalendarGrid,
  CalendarToolbarRow,
  CenteredRow,
  Column,
  FieldControl,
  FieldControlRow,
  FieldLabel,
  FieldValue,
  ListRow,
  PageRoot,
  Plot,
  Row,
  SpacedRow,
  Stack,
  TableCell,
  TableHeader,
  TableRow,
  TabsRow,
  Toolbar,
  ToolbarSearch,
} from './index.styles';

/**
 * A slot that is either off (`false`/`undefined`), on at its default width
 * (`true`), or on at an explicit width (a number of pixels or a CSS length).
 */
type Slot = boolean | SkeletonSize | undefined;

const isSlotOn = (slot: Slot) => slot !== undefined && slot !== false;

const slotWidth = (slot: Slot, fallback: SkeletonSize) =>
  slot === true || slot === undefined ? fallback : (slot as SkeletonSize);

/**
 * A heading: text or an element renders for real, `true` renders a placeholder
 * at the default width, and a number sets that width in pixels. Titles and
 * captions that are static in the loaded view belong in the skeleton too — a
 * header that doesn't move tells the reader what is coming.
 */
type HeadingSlot = React.ReactNode;

const isPlaceholder = (slot: HeadingSlot): slot is boolean | number =>
  slot === true || typeof slot === 'number';

const range = (length: number) => Array.from({ length }, (_, index) => index);

/** Bar heights cycle through this ramp so a chart placeholder reads as data. */
const BAR_HEIGHTS = [58, 84, 41, 96, 67, 78, 50, 89, 62, 73, 45, 92];

/** Events per day column, cycled so a calendar placeholder reads as a schedule. */
const EVENTS_PER_DAY = [2, 1, 3, 2, 4, 1, 2];

export type SkeletonGridSize = { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };

/**
 * Layout helpers for composing one-off shapes: `SkeletonRow` is a horizontal
 * run (button groups, chip rows), `SkeletonColumn` a stacked pair of lines.
 */
export { CenteredRow, Column as SkeletonColumn, Row as SkeletonRow } from './index.styles';

export interface SkeletonCardProps
  extends Pick<ContentCardProps, 'variant' | 'padding' | 'maxWidth' | 'fillHeight'> {
  /** Body content. Falls back to `height`, then to `lines`, when omitted. */
  children?: React.ReactNode;
  title?: HeadingSlot;
  caption?: HeadingSlot;
  /** Trailing header controls: a count of icon buttons, or an explicit node. */
  headerActions?: number | React.ReactNode;
  /** Footer buttons, as a count. */
  footerActions?: number;
  /** Body shorthand: a run of text lines. */
  lines?: number;
  /** Body shorthand: a single block of this height, in pixels. */
  height?: number;
  /** The page above this card. Drawn for real — a back button waits on nothing. */
  backButtonHref?: string;
  label?: string;
}

/**
 * Card placeholder built on the real `ContentCard`, so the border, radius and
 * padding match the loaded card exactly and only its content is stood in for.
 */
export const SkeletonCard = ({
  children,
  title = true,
  caption,
  headerActions = 0,
  footerActions = 0,
  lines = 3,
  height,
  variant,
  padding,
  maxWidth,
  fillHeight,
  backButtonHref,
  label,
}: SkeletonCardProps) => {
  const body =
    children ?? (height ? <SkeletonBox height={height} /> : <SkeletonParagraph lines={lines} />);
  const titleWidth = typeof title === 'number' ? title : 160;
  const captionWidth = typeof caption === 'number' ? caption : 240;
  const heading = isPlaceholder(title) ? <SkeletonHeading width={titleWidth} /> : title;
  const renderHeaderActions = () => {
    if (typeof headerActions !== 'number') {
      return headerActions;
    }
    // An empty Row would still make ContentCard draw a header strip.
    if (headerActions === 0) {
      return undefined;
    }
    return (
      <Row>
        {range(headerActions).map(index => (
          <SkeletonIconButton key={index} />
        ))}
      </Row>
    );
  };
  return (
    <SkeletonGroup fill={fillHeight} label={label}>
      <ContentCard
        variant={variant}
        padding={padding}
        maxWidth={maxWidth}
        fillHeight={fillHeight}
        title={heading}
        backButtonHref={backButtonHref}
        caption={
          isPlaceholder(caption) ? <SkeletonText variant='caption' width={captionWidth} /> : caption
        }
        headerActions={renderHeaderActions()}
        footerActions={
          footerActions > 0 ? (
            <Row>
              {range(footerActions).map(index => (
                <SkeletonButton key={index} />
              ))}
            </Row>
          ) : undefined
        }
      >
        {body}
      </ContentCard>
    </SkeletonGroup>
  );
};

export interface SkeletonFieldRowsProps {
  /**
   * Grid sizes of the fields being stood in for — pass the same shape given to
   * `CommitForm` so the placeholder wraps exactly as the form will.
   */
  fields?: SkeletonGridSize[];
  /** Number of half-width fields, when every field is the same size. */
  count?: number;
  /**
   * `input` draws each value as an open field, capped at `SELECT_WIDTH`, for a
   * form whose rows are always fields rather than text a click opens.
   */
  variant?: 'text' | 'input';
  label?: string;
}

/** Label-and-value rows matching a `CommitForm` grid. */
export const SkeletonFieldRows = ({
  fields,
  count = 4,
  variant = 'text',
  label,
}: SkeletonFieldRowsProps) => {
  const sizes = fields ?? range(count).map(() => ({ xs: 12, md: 6 }));
  const FieldRow = variant === 'input' ? FieldControlRow : Row;

  return (
    <SkeletonGroup label={label}>
      <Grid container spacing={1}>
        {sizes.map((size, index) => (
          <Grid size={size} key={index}>
            <FieldRow>
              <FieldLabel>
                <SkeletonText variant='subtitle2' width={getVariedWidth(index + 1)} />
              </FieldLabel>
              <FieldValue>
                {variant === 'input' ? (
                  <FieldControl>
                    <SkeletonInput height={SELECT_HEIGHT} />
                  </FieldControl>
                ) : (
                  <SkeletonText variant='body2' width={getVariedWidth(index + 3)} />
                )}
              </FieldValue>
            </FieldRow>
          </Grid>
        ))}
      </Grid>
    </SkeletonGroup>
  );
};

/**
 * A column as a share of the width left over, or a `DataGrid` column's fixed
 * width in pixels. Either can be left blank, like a table's trailing space or
 * a column of row actions that most rows leave empty.
 */
export type SkeletonTableColumn =
  | number
  | { width: number; blank?: true }
  | { flex: number; blank: true };

const columnFlex = (column: SkeletonTableColumn) =>
  typeof column === 'number' ? column : 'flex' in column ? column.flex : 0;
const columnWidth = (column: SkeletonTableColumn) =>
  typeof column === 'object' && 'width' in column ? column.width : undefined;
const isBlank = (column: SkeletonTableColumn) => typeof column === 'object' && 'blank' in column;

export interface SkeletonTableProps {
  /** Column count, or each column's share, width or blank space for uneven columns. */
  columns?: number | SkeletonTableColumn[];
  rows?: number;
  showHeader?: boolean;
  rowHeight?: number;
  label?: string;
}

/** Table placeholder matching `DataGrid`'s row heights, striping and rounding. */
export const SkeletonTable = ({
  columns = 4,
  rows = 6,
  showHeader = true,
  rowHeight = TABLE_ROW_HEIGHT,
  label,
}: SkeletonTableProps) => {
  const ratios: SkeletonTableColumn[] =
    typeof columns === 'number' ? range(columns).map(() => 1) : columns;

  return (
    <SkeletonGroup label={label}>
      {showHeader && (
        <TableHeader>
          {ratios.map((column, index) => (
            <TableCell key={index} $flex={columnFlex(column)} $width={columnWidth(column)}>
              {!isBlank(column) && (
                <SkeletonText variant='subtitle2' width={getVariedWidth(index)} />
              )}
            </TableCell>
          ))}
        </TableHeader>
      )}
      <div>
        {range(rows).map(rowIndex => (
          <TableRow
            key={rowIndex}
            $height={rowHeight}
            $first={rowIndex === 0}
            $last={rowIndex === rows - 1}
          >
            {ratios.map((column, columnIndex) => (
              <TableCell key={columnIndex} $flex={columnFlex(column)} $width={columnWidth(column)}>
                {!isBlank(column) && (
                  <SkeletonText
                    variant='body2'
                    width={getVariedWidth(rowIndex + columnIndex * 2)}
                  />
                )}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </div>
    </SkeletonGroup>
  );
};

export interface SkeletonListProps {
  rows?: number;
  /** Leading avatar, or its diameter in pixels. */
  avatar?: boolean | number;
  /** Second, shorter line under the primary one. */
  secondary?: boolean;
  /** Trailing control, or its width. */
  trailing?: Slot;
  dividers?: boolean;
  rowHeight?: number;
  label?: string;
}

/** Rows of `avatar — primary/secondary text — trailing control`. */
export const SkeletonList = ({
  rows = 4,
  avatar = true,
  secondary = true,
  trailing,
  dividers = false,
  rowHeight,
  label,
}: SkeletonListProps) => (
  <SkeletonGroup label={label}>
    {range(rows).map(index => (
      <ListRow key={index} $divided={dividers} $height={rowHeight}>
        {avatar !== false && <SkeletonCircle size={avatar === true ? 40 : avatar} />}
        <Column>
          <SkeletonText variant='body1' width={getVariedWidth(index)} />
          {secondary && <SkeletonText variant='caption' width={getVariedWidth(index + 3)} />}
        </Column>
        {isSlotOn(trailing) && <SkeletonChip width={slotWidth(trailing, 72)} />}
      </ListRow>
    ))}
  </SkeletonGroup>
);

export interface SkeletonPageProps {
  children: React.ReactNode;
  title?: HeadingSlot;
  caption?: HeadingSlot;
  /** Header action buttons, as a count. */
  buttons?: number;
  /** Holds the place of the header's menu, which `ContentWrapper` draws after its buttons. */
  menu?: boolean;
  /** The page above this one. Drawn for real — a back button waits on nothing. */
  backButtonHref?: string;
  maxWidth?: number;
  dense?: boolean;
  label?: string;
}

/** Page placeholder matching `ContentWrapper`'s title, caption and spacing. */
export const SkeletonPage = ({
  children,
  title = true,
  caption,
  buttons = 0,
  menu = false,
  backButtonHref,
  maxWidth,
  dense = false,
  label,
}: SkeletonPageProps) => {
  const titled = title !== undefined && title !== false;

  return (
    <SkeletonGroup label={label}>
      <PageRoot $maxWidth={maxWidth}>
        {titled && (
          <SpacedRow>
            <TitleContainer>
              <BackButton href={backButtonHref} button={WrapperBackButton} />
              {isPlaceholder(title) ? (
                <SkeletonHeading width={typeof title === 'number' ? title : 200} />
              ) : (
                <WrapperTitle variant='h6'>{title}</WrapperTitle>
              )}
            </TitleContainer>
            <Row>
              {range(buttons).map(index => (
                <SkeletonButton key={index} size='small' width={96} />
              ))}
              {menu && <SkeletonIconButton />}
            </Row>
          </SpacedRow>
        )}
        {caption !== undefined && caption !== false && (
          <WrapperCaption>
            {isPlaceholder(caption) ? (
              <SkeletonText variant='caption' width={typeof caption === 'number' ? caption : 320} />
            ) : (
              <Typography variant='caption'>{caption}</Typography>
            )}
          </WrapperCaption>
        )}
        <WrapperContent $titled={titled} $dense={dense}>
          {children}
        </WrapperContent>
      </PageRoot>
    </SkeletonGroup>
  );
};

export interface SkeletonTabsProps {
  tabs?: number;
  /** Matches `Tabs`' own `reserveBadgeRoom`. */
  reserveBadgeRoom?: boolean;
  label?: string;
}

/** Tab bar placeholder matching the spacing of the `Tabs` selector. */
export const SkeletonTabs = ({ tabs = 3, reserveBadgeRoom = false, label }: SkeletonTabsProps) => (
  <SkeletonGroup label={label}>
    <TabsRow $reserveBadgeRoom={reserveBadgeRoom}>
      {range(tabs).map(index => (
        <SkeletonChip key={index} width={88 + (index % 3) * 16} size='medium' />
      ))}
    </TabsRow>
  </SkeletonGroup>
);

export interface SkeletonSelectProps {
  /** Matches `Select`'s own `fullWidth`. */
  fullWidth?: boolean;
  /** Matches `Select`'s own `width`. Ignored while `fullWidth`. */
  width?: number;
  label?: string;
}

/** Placeholder for a `Select` or `MultipleSelect` dropdown. */
export const SkeletonSelect = ({
  fullWidth = false,
  width = SELECT_WIDTH,
  label,
}: SkeletonSelectProps) => (
  <SkeletonGroup label={label}>
    <SkeletonInput width={fullWidth ? '100%' : width} height={SELECT_HEIGHT} />
  </SkeletonGroup>
);

export interface SkeletonToolbarProps {
  /** Search field placeholder filling the leading space. */
  search?: boolean;
  /** Leading action buttons, as a count. */
  leadingActions?: number;
  /** Trailing action buttons, as a count. */
  actions?: number;
  label?: string;
}

/** Search-and-actions bar matching `ContentGroupsList`'s control bar. */
export const SkeletonToolbar = ({
  search = true,
  leadingActions = 0,
  actions = 1,
  label,
}: SkeletonToolbarProps) => (
  <SkeletonGroup label={label}>
    <Toolbar>
      {range(leadingActions).map(index => (
        <SkeletonButton key={index} size='small' width={132} />
      ))}
      <ToolbarSearch>{search && <SkeletonInput height={40} />}</ToolbarSearch>
      {range(actions).map(index => (
        <SkeletonButton key={index} size='small' width={104} />
      ))}
    </Toolbar>
  </SkeletonGroup>
);

export interface SkeletonChartProps {
  variant?: 'bar' | 'line' | 'donut';
  height?: number;
  /** Bars, or x-axis ticks for a line chart. */
  points?: number;
  /** Tick labels below the plot. */
  showAxis?: boolean;
  label?: string;
}

export const SkeletonChart = ({
  variant = 'bar',
  height = 220,
  points = 8,
  showAxis = true,
  label,
}: SkeletonChartProps) => {
  if (variant === 'donut') {
    return (
      <SkeletonGroup label={label}>
        <CenteredRow>
          <SkeletonCircle size={height} />
        </CenteredRow>
      </SkeletonGroup>
    );
  }

  return (
    <SkeletonGroup label={label}>
      {variant === 'bar' ? (
        <Plot $height={height}>
          {range(points).map(index => (
            <SkeletonBox
              key={index}
              grow
              height={`${BAR_HEIGHTS[index % BAR_HEIGHTS.length]}%`}
              radius='control'
            />
          ))}
        </Plot>
      ) : (
        <SkeletonBox height={height} radius='card' />
      )}
      {showAxis && (
        <Axis>
          {/* One tick per bar, so the labels sit under the columns they belong to. */}
          {range(points).map(index => (
            <SkeletonText key={index} variant='caption' grow />
          ))}
        </Axis>
      )}
    </SkeletonGroup>
  );
};

export interface SkeletonStatsProps {
  /** Number of figures in the row. */
  count?: number;
  /** Grid size of each figure. */
  size?: SkeletonGridSize;
  label?: string;
}

/** Row of labelled figures, as used by analytics summaries. */
export const SkeletonStats = ({
  count = 4,
  size = { xs: 6, md: 3 },
  label,
}: SkeletonStatsProps) => (
  <SkeletonGroup label={label}>
    <Grid container spacing={2}>
      {range(count).map(index => (
        <Grid size={size} key={index}>
          <Column>
            <SkeletonText variant='caption' width={index % 2 === 0 ? 92 : 76} />
            <SkeletonText variant='h4' width={index % 3 === 0 ? 72 : 56} />
          </Column>
        </Grid>
      ))}
    </Grid>
  </SkeletonGroup>
);

export interface SkeletonCalendarProps {
  days?: number;
  eventHeight?: number;
  showDayHeadings?: boolean;
  /** `WeekCalendar` always renders a Today button and week arrows above the grid. */
  showToolbar?: boolean;
  label?: string;
}

export const SkeletonCalendar = ({
  days = 7,
  eventHeight = 56,
  showDayHeadings = true,
  showToolbar = true,
  label,
}: SkeletonCalendarProps) => (
  <SkeletonGroup label={label}>
    {showToolbar && (
      <CalendarToolbarRow>
        <SkeletonButton size='small' width={72} />
        <SkeletonIconButton size={32} />
        <SkeletonIconButton size={32} />
      </CalendarToolbarRow>
    )}
    <CalendarGrid $columns={days}>
      {range(days).map(dayIndex => (
        <CalendarColumn key={dayIndex}>
          {showDayHeadings && <SkeletonText variant='overline' width='70%' />}
          {range(EVENTS_PER_DAY[dayIndex % EVENTS_PER_DAY.length]).map(eventIndex => (
            <SkeletonBox key={eventIndex} height={eventHeight} radius='control' />
          ))}
        </CalendarColumn>
      ))}
    </CalendarGrid>
  </SkeletonGroup>
);

export interface SkeletonDetailsPanelProps {
  children?: React.ReactNode;
  title?: HeadingSlot;
  caption?: HeadingSlot;
  /** Trailing header controls, as a count of icon buttons. */
  actions?: number;
  /** Field rows shown when no children are given. */
  fields?: number;
  label?: string;
}

/**
 * Header-and-details placeholder for the right-hand `SidePanel` content. The
 * leading circle stands in for the panel's close button, which every panel
 * puts to the left of an `h6` heading.
 */
export const SkeletonDetailsPanel = ({
  children,
  title = true,
  caption,
  actions = 0,
  fields = 5,
  label = 'Loading details',
}: SkeletonDetailsPanelProps) => (
  <SkeletonGroup label={label}>
    <Stack $gap={2}>
      <SpacedRow>
        <Row>
          <SkeletonIconButton />
          <Column>
            {isPlaceholder(title) ? (
              <SkeletonHeading width={typeof title === 'number' ? title : 200} />
            ) : (
              <Typography variant='h6'>{title}</Typography>
            )}
            {isPlaceholder(caption) ? (
              <SkeletonText variant='caption' width={typeof caption === 'number' ? caption : 140} />
            ) : (
              caption !== undefined &&
              caption !== null && <Typography variant='caption'>{caption}</Typography>
            )}
          </Column>
        </Row>
        <Row>
          {range(actions).map(index => (
            <SkeletonIconButton key={index} />
          ))}
        </Row>
      </SpacedRow>
      {/* A side panel is too narrow for the two-up grid a card uses. */}
      {children ?? <SkeletonFieldRows fields={range(fields).map(() => ({ xs: 12 }))} />}
    </Stack>
  </SkeletonGroup>
);

export interface SkeletonCardGridProps {
  /** Number of tiles. */
  count?: number;
  /** Grid size of each tile. */
  size?: SkeletonGridSize;
  /** Media band above the tile's text. */
  mediaHeight?: number;
  /** Lines of text below the media band. */
  lines?: number;
  label?: string;
}

/** Responsive grid of media tiles, as used by the video and notes libraries. */
export const SkeletonCardGrid = ({
  count = 6,
  size = { xs: 12, sm: 6, md: 4 },
  mediaHeight = 140,
  lines = 2,
  label,
}: SkeletonCardGridProps) => (
  <SkeletonGroup label={label}>
    <Grid container spacing={2}>
      {range(count).map(index => (
        <Grid size={size} key={index}>
          <Stack $gap={1}>
            <SkeletonBox height={mediaHeight} radius='card' />
            <SkeletonText variant='subtitle2' width={getVariedWidth(index)} />
            {lines > 1 && <SkeletonText variant='caption' width={getVariedWidth(index + 2)} />}
          </Stack>
        </Grid>
      ))}
    </Grid>
  </SkeletonGroup>
);
